import { z } from 'zod';
import { Cents, PlainDate } from './common.js';

const Count = z.number().int().nonnegative();
const DateTime = z.iso.datetime({ offset: true });
const AwardRef = z.object({ id: z.uuid(), displayId: z.string() });

/**
 * One person's tax as ADP Quick Calc gives it (APP-101): the taxes on their cash, saved as the
 * figure for every choice. Always an estimate; Payroll sets the final tax.
 */
export const TaxEntryLine = z.object({
  lineId: z.uuid(),
  personId: z.uuid(),
  name: z.string(),
  eeNumber: z.string().nullable(),
  /** The gross Quick Calc is run on: the person's cash on the award. */
  cashCents: Cents,
  /** The taxes entered; null until someone enters them. */
  taxCents: Cents.nullable(),
  /** Who last entered the figure and when; null while there's no figure. */
  entered: z.object({ by: z.string(), at: DateTime }).nullable(),
  /** ADP screenshots on file for this person alone; staff-only. */
  screenshots: Count,
  /** The signed-in Stock Admin may enter or change it (before release). */
  canChange: z.boolean(),
});
export type TaxEntryLine = z.infer<typeof TaxEntryLine>;

/** One PAA's people on the Tax to enter list, the check and Letters. */
export const TaxEntryAward = z.object({
  award: AwardRef,
  title: z.string(),
  sector: z.string().nullable(),
  awardDate: PlainDate.nullable(),
  approvedAt: DateTime.nullable(),
  fmvCents: Cents.nullable(),
  /** The letters have been released: every figure is locked. */
  lettersOut: z.boolean(),
  /** ADP screenshots attached to the whole award, not to one person (an older check's). */
  awardScreenshots: Count,
  /** People still without a figure. */
  toEnter: Count,
  people: z.array(TaxEntryLine),
});
export type TaxEntryAward = z.infer<typeof TaxEntryAward>;

/** The Tax to enter list: awards with someone to enter first, oldest approval first. */
export const TaxEntryList = z.object({
  asOf: DateTime,
  toEnter: Count,
  awards: z.array(TaxEntryAward),
  /** Some PAA has reached the Stock Admin team: an empty list is caught up, not the first day. */
  reachedTeam: z.boolean(),
});
export type TaxEntryList = z.infer<typeof TaxEntryList>;

/**
 * Enter or change one person's taxes. The screen sends the gross and the figure it showed, so a
 * change by someone else, or a new gross from taking the values again, is refused, not overwritten.
 */
export const SaveTaxEntry = z
  .object({
    cashCents: Cents.nonnegative(),
    previousTaxCents: Cents.nonnegative().nullable(),
    taxCents: Cents.nonnegative(),
  })
  .strict();
export type SaveTaxEntry = z.infer<typeof SaveTaxEntry>;
